import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService, STEP } from 'src/app/services/login.service';
import { IResponseCelphoneNumber } from 'src/app/utils/interfaces/celphone.interface';
import { RESPONSE_RUT } from 'src/app/utils/enums/rut.enum';
import { IBodyPersonalData } from 'src/app/utils/interfaces/personal-data.interface';
import { IBodyPasswordLogin } from 'src/app/utils/interfaces/login.interface';
import { RESPONSE_LOGIN } from 'src/app/utils/enums/login.enum';
import { RESPONSE_FORGOT } from 'src/app/utils/enums/forgot.enum';
import { RESPONSE_NEW_PASSWORD } from 'src/app/utils/enums/new-password.enum';
import { RESPONSE_CODE } from 'src/app/utils/enums/code.enum';
import { IScope } from 'src/app/utils/interfaces/scope.interface';
import { RESPONSE_VALIDA } from 'src/app/utils/enums/valida.enum';
import { WindowRefService } from 'src/app/services/window.service';
import { RESPONSE_FORGOT_RUT } from 'src/app/utils/enums/forgot-rut.enum';
import { IBodyValidator } from 'src/app/utils/interfaces/validator.interface';
import { AlertModalComponent } from './alert-modal/alert-modal.component';
import { ToastNotificationComponent } from '../../components/atoms/toast-notification/toast-notification.component';
import { StepConfig, StepOutletDirective } from './step-outlet.directive';

@Component({
  selector: 'app-init',
  templateUrl: './init.component.html',
  standalone: true,
  providers: [DatePipe],
  imports: [
    CommonModule,
    AlertModalComponent,
    ToastNotificationComponent,
    StepOutletDirective,
  ],
})
export class InitComponent {
  public service = inject(LoginService);
  public router = inject(Router);
  public loadingStep = false;
  public userNumber!: string;
  public userRut!: string;
  public userName!: string;
  public userEmail!: string;
  public userLastName!: string;
  public userBirth!: string;
  public scopes: IScope[] = [];
  public remainingAttempts: number | null = null;
  public readonly STEP = STEP;
  private _windowRef = inject(WindowRefService);
  private datePipe = inject(DatePipe);
  private bodyPersonalData?: IBodyPersonalData;
  private passwordCode!: string;
  public isShowAlert: boolean = false;
  public message: string = 'Lo sentimos, hoy has alcanzado el número máximo de reintentos.';
  public buttonText: string = 'Volver a intentar';
  public isSuccess: boolean = false;
  public showToast: boolean = false;
  public toastMessage: string = '';
  public readonly steps: Partial<Record<STEP, StepConfig>> = {
    [STEP.CELPHONE_NUMBER]: {
      load: () => import('./celphone-number/celphone-number.component').then((m) => m.CelphoneNumberComponent),
      inputs: () => ({ loading: this.loadingStep }),
      outputs: { onTelNumber: (number) => this.checkCelphoneNumber(number) },
      hostClass: 'md:max-w-[600px] lg:m-auto',
    },
    [STEP.CELPHONE_CODE]: {
      load: () => import('./celphone-code/celphone-code.component').then((m) => m.CelphoneCodeComponent),
      inputs: () => ({ loading: this.loadingStep, remainingAttempts: this.remainingAttempts }),
      outputs: {
        onTelCode: (code) => this.checkCelphoneCode(code),
        resendCode: (type) => this.checkCelphoneNumber(this.userNumber, type),
      },
    },
    [STEP.FORGOT_RUT_CODE]: {
      load: () => import('./forgot-rut-code/forgot-rut-code.component').then((m) => m.ForgotRutCodeComponent),
      inputs: () => ({ userNumber: this.userNumber, loading: this.loadingStep }),
      outputs: {
        onTelCode: (code) => this.checkForgot(code),
        resendCode: () => this.checkForgotRut(this.userRut),
      },
    },
    [STEP.EMAIL]: {
      load: () => import('./email/email.component').then((m) => m.EmailComponent),
      inputs: () => ({ loading: this.loadingStep }),
      outputs: { onEmail: (mail) => this.checkEmail(mail) },
    },
    [STEP.RUT]: {
      load: () => import('./rut/rut.component').then((m) => m.RutComponent),
      inputs: () => ({ loading: this.loadingStep, userRut: this.userRut }),
      outputs: { onRut: (rut) => this.checkRut(rut) },
    },
    [STEP.PERSONAL_DATA]: {
      load: () => import('./personal-info/personal-info.component').then((m) => m.PersonalInfoComponent),
      inputs: () => ({
        loading: this.loadingStep,
        userName: this.userName,
        userLastName: this.userLastName,
        userBirth: this.userBirth,
      }),
      outputs: { onPersonalInfo: (data) => this.checkPersonalInfo(data) },
    },
    [STEP.PASSWORD]: {
      load: () => import('./password/password.component').then((m) => m.PasswordComponent),
      inputs: () => ({ loading: this.loadingStep }),
      outputs: { onPassword: (password) => this.checkPassword(password) },
    },
    [STEP.NEW_PASSWORD]: {
      load: () => import('./new-password/new-password.component').then((m) => m.NewPasswordComponent),
      inputs: () => ({ loading: this.loadingStep }),
      outputs: { onNewPassword: (password) => this.checkNewPassword(password) },
    },
    [STEP.SUCCESS]: {
      load: () => import('./success/success.component').then((m) => m.SuccessComponent),
      outputs: { onSuccess: () => this.service.setStep(STEP.TERMS) },
    },
    [STEP.SELECT_ACCOUNT]: {
      load: () => import('./select-account/select-account.component').then((m) => m.SelectAccountComponent),
      inputs: () => ({ loading: this.loadingStep }),
      outputs: {
        onLogout: () => this.onLogout(),
        onContinue: () => this.accountSelected(),
      },
    },
    [STEP.TERMS]: {
      load: () => import('./terms/terms.component').then((m) => m.TermsComponent),
      inputs: () => ({ loading: this.loadingStep }),
      outputs: {
        onContinue: () => this.checkValidation(),
        onLogout: () => this.onLogout(),
      },
    },
    [STEP.WELCOME]: {
      load: () => import('./welcome-password/welcome-password.component').then((m) => m.WelcomePasswordComponent),
      inputs: () => ({ userName: this.userName, loading: this.loadingStep }),
      outputs: {
        onLogin: (password) => this.checkWelcomePassword(password),
        onForgot: () => this.forgotPassword(),
      },
    },
    [STEP.FORGOT]: {
      load: () => import('./forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
      inputs: () => ({ userEmail: this.userEmail, loading: this.loadingStep }),
      outputs: {
        onForgotPassword: (code) => this.checkForgot(code),
        onResendCode: () => this.forgotPassword(),
        onForgotRut: () => this.service.setStep(STEP.FORGOT_RUT),
      },
    },
    [STEP.FORGOT_RUT]: {
      load: () => import('./forgot-rut/forgot-rut.component').then((m) => m.ForgotRutComponent),
      inputs: () => ({ loading: this.loadingStep }),
      outputs: { onForgotRut: (rut) => this.checkForgotRut(rut) },
    },
    [STEP.RECOGNIZED]: {
      load: () => import('./recognized/recognized.component').then((m) => m.RecognizedComponent),
      outputs: { onDownload: () => this.checkDownload() },
    },
    [STEP.RECOVERY]: {
      load: () => import('./recovery/recovery.component').then((m) => m.RecoveryComponent),
    },
    [STEP.OOPS]: {
      load: () => import('./oops/oops.component').then((m) => m.OopsComponent),
    },
    [STEP.SUCCESSQR]: {
      load: () => import('./success-qr/success-qr.component').then((m) => m.SuccessQrComponent),
    },
  };

  public checkCelphoneNumber(number: any, type?: 'sms' | 'voice'): void {
    this.loadingStep = true;
    const newNumber = '56' + Number(number.replace('+', ''));
    
    // Show toast when resending code
    if (type) {
      this.showToastNotification(type);
    }
    
    this.service
      .setCelphone({
        telefono: Number(newNumber),
        tipoMensaje: type ? type : 'sms',
      })
      .subscribe({
        next: (resp: IResponseCelphoneNumber) => {
          this.service.accessToken = resp.access_token
            ? resp.access_token
            : this.service.accessToken;
          if (resp.codigoRespuesta === 2) {
            this.userNumber = number;
            this.service.setStep(STEP.CELPHONE_CODE);
          } else if (resp.codigoRespuesta === 1) {
            this.showAlertModal(
              'Lo sentimos, hoy has alcanzado el número máximo de reintentos.'
            );
          }
        },
        complete: () => {
          this.loadingStep = false;
        },
        error: (err) => {
          this.checkErr(err.status);
          this.loadingStep = false;
        },
      });
  }

  public forgotPassword(): void {
    this.loadingStep = true;
    this.service.setForgot({}).subscribe({
      next: (resp: any) => {
        switch (resp.codigoRespuesta as RESPONSE_FORGOT) {
          case RESPONSE_FORGOT.CANTIDAD_REINTENTOS_SUPERADA:
            this.showAlertModal('Superaste la cantidad de reintentos');
            break;
          case RESPONSE_FORGOT.CORREO_ENVIADO:
            this.service.setStep(STEP.FORGOT);
            break;
        }
      },
      complete: () => {
        this.loadingStep = false;
      },
      error: (err) => {
        this.checkErr(err.status);
        this.loadingStep = false;
      },
    });
  }

  public checkForgotRut(rut: string): void {
    const numeroSerieRut = rut.slice(-1);
    const newRut = rut.replace(/[.-]/g, '').substring(0, 9);
    this.loadingStep = true;
    this.userRut = newRut;
    this.service
      .setForgotRut({ rut: newRut, numeroSerieRut: '', tipoMensaje: 'sms' })
      .subscribe({
        next: (resp: any) => {
          switch (resp.codigoRespuesta as RESPONSE_FORGOT_RUT) {
            case RESPONSE_FORGOT_RUT.CANTIDAD_REINTENTOS_SUPERADA:
              this.showAlertModal('Cantidad de intentos superada');
              break;
            case RESPONSE_FORGOT_RUT.FORMATO_RUT_INCORRECTO:
              this.showAlertModal('Formato de rut incorrecto');
              break;
            case RESPONSE_FORGOT_RUT.RUT_INVALIDO:
              this.showAlertModal('Rut invalido');
              break;
            case RESPONSE_FORGOT_RUT.RUT_NO_CORRESPONDE:
              this.showAlertModal('Rut no corresponde');
              break;
            case RESPONSE_FORGOT_RUT.SMS_ENVIADO:
              this.service.setStep(STEP.FORGOT_RUT_CODE);
              break;
            case RESPONSE_FORGOT_RUT.TELEFONO_NO_CORRESPONDE:
              this.showAlertModal('Telefono no corresponde');
              break;
          }
        },
        complete: () => {
          this.loadingStep = false;
        },
        error: (err) => {
          this.checkErr(err.status);
          this.loadingStep = false;
        },
      });
  }

  public checkCelphoneCode(code: any): void {
    this.loadingStep = true;
    this.service.setCelphoneCode({ sms: code }).subscribe({
      next: (resp: any) => {
        switch (resp.codigoRespuesta as RESPONSE_CODE) {
          case RESPONSE_CODE.CADUCADO:
            this.showAlertModal('Sms caducado, vuelve a intentarlo');
            break;
          case RESPONSE_CODE.INCORRECT_SMS:
            this.remainingAttempts = resp.intentosRestantes;

            this.showAlertModal(
              'El código SMS que ingresaste es incorrecto.'
            );
            break;
          case RESPONSE_CODE.MAX:
            this.remainingAttempts = 0;

            this.showAlertModal(
              'Lo sentimos, hoy has alcanzado el número máximo de reintentos'
            );
            break;
          case RESPONSE_CODE.USER_EXISTS:
            this.userName = resp.usuario.nombre;
            this.userEmail = resp.usuario.mail;
            this.service.setStep(STEP.WELCOME);
            this.service.accessToken = resp.access_token;
            break;
          case RESPONSE_CODE.USER_NON_EXISTS:
            this.service.setStep(STEP.EMAIL);
            this.service.accessToken = resp.access_token;
            break;
        }
      },
      complete: () => {
        this.loadingStep = false;
      },
      error: (err) => {
        this.checkErr(err.status);
        this.loadingStep = false;
      },
    });
  }

  public checkRut(rut: string): void {
    const numeroSerieRut = rut.slice(-1);
    const newRut = rut.replace(/[.-]/g, '').substring(0, 9);
    this.loadingStep = true;
    this.service.setRut({ rut: newRut, numeroSerieRut: '' }).subscribe({
      next: (resp: any) => {
        this.userRut = rut;
        if (resp.access_token) {
          this.service.accessToken = resp.access_token;
        }
        if (resp.tyc) {
          this.service.tycCopec = resp.tyc;
        }
        switch (resp.codigoRespuesta as RESPONSE_RUT) {
          case RESPONSE_RUT.CANTIDAD_REINTENTOS_SUPERADA:
            this.showAlertModal('Superaste la cantidad de reintentos');
            break;
          case RESPONSE_RUT.FORMATO_RUT_INCORRECTO:
            this.showAlertModal('Rut incorrecto');
            break;
          case RESPONSE_RUT.RUT_DISPONIBLE:
            this.service.setStep(STEP.PERSONAL_DATA);
            break;
          case RESPONSE_RUT.RUT_INVALIDO:
            this.showAlertModal('Rut invalido');
            break;
          case RESPONSE_RUT.RUT_OCUPADO:
            this.service.setStep(STEP.RECOVERY);
            break;
        }
      },
      complete: () => {
        this.loadingStep = false;
      },
      error: (err) => {
        this.checkErr(err.status);
        this.loadingStep = false;
      },
    });
  }

  public checkNewPassword(password: string): void {
    this.loadingStep = true;
    this.service
      .setForgotPassword({ codigo: this.passwordCode, password })
      .subscribe({
        next: (resp: any) => {
          switch (resp.codigoRespuesta as RESPONSE_NEW_PASSWORD) {
            case RESPONSE_NEW_PASSWORD.CANTIDAD_REINTENTOS_SUPERADA:
              this.showAlertModal('Cantidad de intentos superada');
              break;
            case RESPONSE_NEW_PASSWORD.CODIGO_CADUCADO:
              this.showAlertModal('Codigo caducado');
              this.service.setStep(STEP.FORGOT);
              break;
            case RESPONSE_NEW_PASSWORD.CODIGO_INCORRECTO:
              this.showAlertModal('Codigo incorrecto, intente nuevamente');
              this.service.setStep(STEP.FORGOT);
              break;
            case RESPONSE_NEW_PASSWORD.CONTRASEÑA_MODIFICADA:
              this.userName = resp.usuario.nombre;
              this.showAlertModal('Contraseña modificada exitosamente','Continuar', true);
              this.service.setStep(STEP.WELCOME);
              break;
          }
        },
        complete: () => {
          this.loadingStep = false;
        },
        error: (err) => {
          this.checkErr(err.status);
          this.loadingStep = false;
        },
      });
  }

  public checkEmail(mail: string): void {
    this.loadingStep = true;
    this.service.setEmail({ mail }).subscribe({
      next: (resp: any) => {
        if (resp.codigoRespuesta === 1) {
          this.showAlertModal('Formato de correo incorrecto');
        } else if (resp.codigoRespuesta === 2) {
          this.service.accessToken = resp.access_token;
          this.service.setStep(STEP.RUT);
        } else if (resp.codigoRespuesta === 3) {
          this.service.accessToken = resp.access_token;
          this.service.setStep(STEP.RECOVERY);
        }
      },
      complete: () => {
        this.loadingStep = false;
      },
      error: (err) => {
        this.checkErr(err.status);
      },
    });
  }

  public checkValidation(isCancel?: boolean): void {
    this.loadingStep = true;
    if (this.scopes.length <= 0) {
      this.scopes = this.service.scopes;
    }
    let body: IBodyValidator = { scopes: this.scopes };
    if (isCancel) {
      body = { ...body, cancelar: 1 };
    }
    if (this.service.tycClient.isTermsRequired) {
      body = {
        ...body,
        terminos_y_condiciones_version: this.service.tycClient.termsVersion,
      };
    }
    if (this.service.tycClient.isPolicyRequired) {
      body = {
        ...body,
        politica_privacidad_version: this.service.tycClient.policyVersion,
      };
    }
    this.service.setValidation(body).subscribe({
      next: (resp: any) => {
        if (resp.codigoRespuesta === 1) {
          this.checkValida();
        } else if (resp.codigoRespuesta === 2) {
           window.open(resp.redirect_url, '_self');
        }
      },
      error: (err) => {
        this.checkErr(err.status);
        this.loadingStep = false;
      },
    });
  }

  public checkPersonalInfo(data: {
    name: string;
    lastname: string;
    birth: string;
  }): void {
    this.userName = data.name;
    this.userLastName = data.lastname;
    this.userBirth = data.birth;
    this.bodyPersonalData = {
      nombre: data.name,
      apellido: data.lastname,
      fechaNacimiento: this.datePipe.transform(data.birth, 'yyyy-MM-dd')!,
      tyc: {
        versionAppCopec: this.service.tycCopec[0].version,
        versionPoliticaPrivacidad: this.service.tycCopec[1].version,
      },
      password: '',
    };
    this.service.setStep(STEP.PASSWORD);
  }

  public checkForgot(passwordCode: string): void {
    this.passwordCode = passwordCode;
    this.service.setStep(STEP.NEW_PASSWORD);
  }

  public checkPassword(password: string): void {
    this.loadingStep = true;
    const body: IBodyPersonalData = {
      ...this.bodyPersonalData,
      password: password,
    } as IBodyPersonalData;
    this.service.setPersonalData(body).subscribe({
      next: (resp: any) => {
        if (resp.codigoRespuesta === 3) {
          this.checkValida();
        } else if (resp.codigoRespuesta === 1) {
          this.showAlertModal('Formato de nombre incorrecto');
        }
      },
      complete: () => {
      },
      error: (err) => {
        this.checkErr(err.status);
        this.loadingStep = false;
      },
    });
  }

  public onLogout(): void {
    this.loadingStep = true;
    this.service.tokenDelete({}).subscribe({
      next: (resp: any) => {
        if (resp.codigoRespuesta === 1) {
          this.service.initializeData();
        }
      },
      complete: () => {
        this.loadingStep = false;
      },
      error: (err) => {
        this.checkErr(err.status);
        this.loadingStep = false;
      },
    });
  }

  public checkWelcomePassword(password: string): void {
    this.loadingStep = true;
    const body: IBodyPasswordLogin = { password } as IBodyPasswordLogin;
    this.service.setLogin(body).subscribe({
      next: (resp: any) => {
        switch (resp.codigoRespuesta as RESPONSE_LOGIN) {
          case RESPONSE_LOGIN.SESION_INICIADA_CON_VALIDA:
            this.checkValida();
            break;
          case RESPONSE_LOGIN.CANTIDAD_REINTENTOS_SUPERADA:
            this.showAlertModal('Superaste la cantidad de reintentos');
            this.loadingStep = false;

            break;
          case RESPONSE_LOGIN.CONTRASEÑA_INCORRECTA:
            this.showAlertModal(
              'Contraseña incorrecta, intenta nuevamente'
            );
            this.loadingStep = false;

            break;
          case RESPONSE_LOGIN.SESION_INICIADA_SIN_VALIDA:
            if (resp.redirect_url) {
              window.open(resp.redirect_url);
            }
            break;
        }
      },
      error: (err) => {
        this.checkErr(err.status);
        this.loadingStep = false;
      },
    });
  }

  public showAlertModal(message: string, buttonText: string = 'Volver a intentar', isSuccess: boolean = false): void {
    this.message = message;
    this.buttonText = buttonText;
    this.isSuccess = isSuccess;
    this.isShowAlert = true;
  }

  public closeModal(): void {
    this.isShowAlert = false;
  }

  public actionClick(): void {
    this.isShowAlert = false;
  }

  public accountSelected(): void {
    this.loadingStep = true;
    window.open(this.service.selectAccountInfo.redirect_url, '_self');
  }

  public checkValida(): void {
    this.loadingStep = true;
    this.service.getValidation().subscribe({
      next: (resp: any) => {
        switch (resp.codigoRespuesta as RESPONSE_VALIDA) {
          case RESPONSE_VALIDA.REDIRECT:
            this.service.accessToken = resp.access_token;
            this._windowRef.nativeWindow.open(resp.redirect_url, '_self');
            break;
          case RESPONSE_VALIDA.VALIDA:
            this.service.selectAccountInfo = resp;
            this.service.accessToken = resp.access_token;
            this.service.tycClient = {
              text: resp.texto,
              policy: resp.politica_privacidad_link,
              terms: resp.terminos_y_condiciones_link,
              alias: resp.alias,
              isPolicyRequired: resp.politica_privacidad_obligatorio === 1,
              isTermsRequired: resp.terminos_y_condiciones_obligatorio === 1,
              termsVersion: resp.terminos_y_condiciones_version,
              policyVersion: resp.politica_privacidad_version,
            };
            this.scopes = resp.scopes;
            this.loadingStep = false;
            this.service.setStep(STEP.TERMS);
            break;
          case RESPONSE_VALIDA.SERVICE_ERROR_REDIRECT:
            this._windowRef.nativeWindow.open(resp.redirect_url, '_self');
            break;
          case RESPONSE_VALIDA.SUCCESS_AUTH_REDIRECT:
            this.service.accessToken = resp.access_token;
            this._windowRef.nativeWindow.open(resp.redirect_url, '_self');
            break;
          default:
            this.loadingStep = false;
            this.showAlertModal('Ocurrió un error, intenta nuevamente');
            break;
        }
      },
      error: (err) => {
        this.checkErr(err.status);
        this.loadingStep = false;
      },
    });
  }

  public checkDownload() {
    //  DOWNLOAD APP
  }

  private checkErr(status: number): void {
    if (status === 403) {
      this.service.initializeData();
    }
  }

  public showToastNotification(type: 'sms' | 'voice'): void {
    if (type === 'sms') {
      this.toastMessage = 'El nuevo SMS debe estar por llegar.';
    } else {
      this.toastMessage = 'Te estamos llamando, tu teléfono debe sonar en 3..2..1..';
    }
    this.showToast = true;
    
    // Reset showToast to false after a brief delay to allow re-triggering
    setTimeout(() => {
      this.showToast = false;
    }, 5500);
  }
}
